import {
  Container,
  TextField,
  Button,
  Box,
  Typography,
  Link,
} from "@mui/material";
import React, { useState } from "react";
import PageTitle from "../../widgets/PageTitle";
import axios from "axios";
import { getHeader } from "../../utils/util";
import { API_ENDPOINT } from "../../utils/constants";

const CreateAuthenticationTemplate = () => {
  const [templateName, setTemplateName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [nameError, setNameError] = useState("");

  const validateName = (name) => {
    // Regular expression to allow only lowercase letters, numbers, and underscores
    const regex = /^[a-z0-9_]+$/;
    if (name.length > 512) {
      return "Name must be 512 characters or less";
    }
    if (name.includes(" ")) {
      return "Spaces are not allowed";
    }
    if (!regex.test(name)) {
      return "Only lowercase letters, numbers, and underscores are allowed";
    }
    return "";
  };

  const handleNameChange = (e) => {
    const value = e.target.value;
    setTemplateName(value);
    setNameError(validateName(value));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validateName(templateName);
    if (validationError) {
      setNameError(validationError);
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setSuccess(false);

    try {
      // Replace with your actual API endpoint
      const response = await axios.post(
        `${API_ENDPOINT}/api/create-authentication-template`,
        {
          name: templateName,
        },
        getHeader()
      );
      setSuccess(true);
      setTemplateName(""); // Reset form after successful submission
    } catch (err) {
      console.error("Error creating template:", err);
      setError(err.response?.data?.message || "Failed to create template");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container maxWidth="md">
      <PageTitle>Create Authentication Template</PageTitle>

      <Typography variant="body1" paragraph>
        Simply provide a name below to create a new authentication template with
        a default OTP button. This template will be pre-configured for one-time
        password authentication flows.
      </Typography>

      <Typography variant="body2" color="text.secondary" paragraph>
        The created template will include standard authentication components:
        <Box component="ul" sx={{ pl: 2, mt: 1 }}>
          <li>OTP (One-Time Password) button</li>
          <li>Standard authentication message structure</li>
          <li>Required security parameters</li>
        </Box>
      </Typography>

      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          mt: 4,
          display: "flex",
          flexDirection: "column",
          gap: 3,
          maxWidth: 600,
          mx: "auto",
        }}
      >
        <TextField
          label="Template Name"
          variant="outlined"
          fullWidth
          value={templateName}
          onChange={handleNameChange}
          required
          helperText={
            nameError ||
            "Only lowercase letters (a-z), numbers (0-9), and underscores (_). No spaces. Max 512 characters."
          }
          error={!!nameError}
          disabled={isSubmitting}
          inputProps={{
            maxLength: 512,
          }}
        />

        {error && (
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        )}

        {success && (
          <Typography color="success.main" variant="body2">
            Template created successfully! Your authentication template with OTP
            button is now ready.
          </Typography>
        )}

        <Box justifyContent={"end"} display={"flex"}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            size="large"
            disabled={isSubmitting || !templateName.trim() || !!nameError}
            sx={{ mt: 2 }}
          >
            {isSubmitting ? "Creating..." : "Create Authentication Template"}
          </Button>
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 2 }}>
          Learn more about authentication templates in the{" "}
          <Link
            href="https://developers.facebook.com/docs/whatsapp/business-management-api/authentication-templates"
            target="_blank"
            rel="noopener noreferrer"
          >
            WhatsApp Business Management API documentation
          </Link>
          .
        </Typography>
      </Box>
    </Container>
  );
};

export default CreateAuthenticationTemplate;
